import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { PortalFeedbackModal, SubmitUserFeedback } from "@bto/shared";
import type { FeedbackSource } from "@bto/shared";
import { useFeedbackForm } from "@bto/shared/hooks/useFeedbackForm";
import { getUser, getUserAuth } from "../../recoil/selectors";
import { BASE_URL } from "../../utils/requestUtils";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDismiss?: () => void;
  source: FeedbackSource;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onDismiss,
  source,
}) => {
  const { t } = useTranslation();
  const { deviceType } = useDeviceHook();
  const user = useRecoilValue(getUser);
  const auth = useRecoilValue(getUserAuth);

  const submit = useCallback(
    async (score: number, comment: string) => {
      const response = await SubmitUserFeedback(
        { score, ...(comment && { comment }), source },
        BASE_URL,
        auth?.access_token,
      );

      if (response.ok) {
        TrackAnalyticsEvent(
          AnalyticsEventNames.NpsSurveySubmitted,
          auth?.access_token,
          auth?.user_id,
          {
            score,
            source,
            has_comment: comment ? "yes" : "no",
          },
        );
      }

      return response;
    },
    [auth?.access_token, auth?.user_id, source],
  );

  const {
    score,
    setScore,
    comment,
    setComment,
    isSubmitting,
    isSubmitted,
    error,
    handleSubmit,
    handleClose,
  } = useFeedbackForm({ submit, onClose, onDismiss });

  return (
    <PortalFeedbackModal
      isOpen={isOpen}
      firstName={user?.first_name}
      score={score}
      comment={comment}
      isSubmitting={isSubmitting}
      isSubmitted={isSubmitted}
      error={error}
      variant={deviceType === "desktop" ? "desktop" : "mobile"}
      introQuestion={t("feedback.introQuestion")}
      onScoreChange={setScore}
      onCommentChange={setComment}
      onSubmit={handleSubmit}
      onClose={handleClose}
      cancelToastMessage={t("feedback.cancelToast")}
    />
  );
};
